/**
 * FrameMasterContext.tsx — Etat global du Frame Master
 * Sprint A — Frame Master V2
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import type { BotInfo } from "../api/types";
import { useAuth } from "./AuthContext";

export type ActiveView = "dashboard" | "cockpit" | "health" | "department" | "detail" | "discussion" | "branches" | "cahier" | "scenarios" | "live-chat" | "canvas" | "orbit9-detail" | "agent-settings" | "espace-bureau" | "blueprint" | "board-room" | "war-room" | "think-room" | "mes-chantiers" | "mon-reseau" | "bible-visuelle" | "bible-technique" | "bible-ghml" | "master-roadmap" | "master-strategie" | "master-orbit9" | "master-communication" | "master-dette" | "master-routine" | "master-minedor" | "master-training" | "master-profils" | "master-parcours" | "master-navigation" | "master-angles-morts" | "master-capacites" | "master-instance-fonds" | "master-diagnostics" | "master-playbooks" | "master-bibliotheque-exec" | "master-marketing-360" | "master-guides-legaux" | "master-cortex-robot" | "master-hydro-quebec" | "master-flows" | "master-cartographie" | "master-oracle9" | "master-bible-live" | "bible-visuelle-cible" | "flow-usine-bleue" | "animation-showcase" | "agent-gallery" | "playbook-usine-bleue" | "fe-sidebar-droite" | "strategique-reseau" | "fe-mon-reseau" | "accueil-hero" | "bible-officielle" | "carlos-codes" | "diagnostic-ia" | "meeting-room" | "conference-ai" | "salles-hub" | "mon-entreprise" | "mon-equipe" | "status" | "onboarding" | "docforge" | "ateliers" | "admin" | "reglages";

export type EspaceSection = "idees" | "projets" | "documents" | "taches" | "outils" | "agenda" | "templates" | "discussions" | "notifications";

export type ReseauSection = "profil" | "cellules" | "jumelage" | "chantiers" | "pionniers" | "gouvernance" | "dashboard" | "nouvelles" | "evenements" | "industrie";

export type BlueprintLiveSection = "live" | "hub" | "pipeline";

export type EntrepriseSection = "blueprint" | "dashboard" | "ressources" | "sante";
export type BlueprintSubTab = "sommaire" | "objectifs" | "chantiers";
export type EquipeSection = "humains" | "bots" | "composition" | "roles" | "performance";
export type BureauSection = "blueprint-perso" | "taches" | "documents" | "agenda" | "discussions" | "notifications" | "claude-code";

export type DiscussionTab = "overview" | "timeline" | "chantiers" | "projets" | "missions" | "taches" | "opportunites" | "equipes" | "discussions";

interface FrameMasterState {
  activeBot: BotInfo | null;
  activeBotCode: string;
  activeView: ActiveView;
  activeOrbit9Section: string | null;
  activeEspaceSection: EspaceSection;
  activeReseauSection: ReseauSection;
  activeBlueprintLiveSection: BlueprintLiveSection;
  activeEntrepriseSection: EntrepriseSection;
  activeBlueprintSubTab: BlueprintSubTab;
  activeEquipeSection: EquipeSection;
  activeBureauSection: BureauSection;
  activeDiscussionTab: DiscussionTab;
  /** Tab actif dans DepartmentTourDeControle (cockpit, chantiers, missions, etc.) */
  activeDeptTab: string;
  /** Tab actif dans AdminPanel (dashboard, tenants, users, etc.) */
  activeAdminTab: string;
  /** D-109 — Source view quand on ouvre le LiveChat depuis une Room */
  chatSourceView: string | null;
  leftSidebarCollapsed: boolean;
  rightSidebarCollapsed: boolean;
  isAuthenticated: boolean;
  isOnboarded: boolean;
  hasSeenSimulation: boolean;
  currentUser: string;
}

interface FrameMasterActions {
  setActiveBot: (bot: BotInfo) => void;
  setActiveView: (view: ActiveView) => void;
  /** Navigate vers un departement par code bot (sans BotInfo complet) */
  navigateToDepartment: (botCode: string, view?: ActiveView) => void;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
  setAuthenticated: (v: boolean) => void;
  setOnboarded: (v: boolean) => void;
  setHasSeenSimulation: (v: boolean) => void;
  setLeftCollapsed: (v: boolean) => void;
  navigateOrbit9: (sectionId: string) => void;
  navigateEspace: (section: EspaceSection) => void;
  navigateReseau: (section: ReseauSection) => void;
  navigateBlueprint: (section: BlueprintLiveSection) => void;
  navigateEntreprise: (section: EntrepriseSection) => void;
  navigateBlueprintSubTab: (tab: BlueprintSubTab) => void;
  navigateEquipe: (section: EquipeSection) => void;
  navigateBureau: (section: BureauSection) => void;
  navigateDiscussionTab: (tab: DiscussionTab) => void;
  /** Navigate vers un tab dans DepartmentTourDeControle */
  navigateDeptTab: (tab: string) => void;
  /** Navigate vers un tab dans AdminPanel */
  navigateAdminTab: (tab: string) => void;
  /** Navigate vers un tab dans AdminPanel mode instance (reglages) */
  navigateReglagesTab: (tab: string) => void;
  /** D-109 — Ouvre le LiveChat avec contexte de la source (board-room, war-room, etc.) */
  navigateToChat: (sourceView: string) => void;
  /** Set active bot code directly (used by CanvasActionContext focus sync) */
  setActiveBotCode: (code: string) => void;
  // Registre pour le panel imperatif
  registerLeftPanel: (api: { collapse: () => void; expand: () => void }) => void;
}

type FrameMasterContextType = FrameMasterState & FrameMasterActions;

const FrameMasterContext = createContext<FrameMasterContextType | null>(null);

export function FrameMasterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeBot, setActiveBotState] = useState<BotInfo | null>(null);
  const [activeBotCode, setActiveBotCode] = useState("CEOB");
  const [activeView, setActiveView] = useState<ActiveView>(() => {
    const p = window.location.pathname;
    if (p.startsWith("/v2-")) return (p.slice(4) as ActiveView) || "department";
    return "department"; // Vue par defaut
  });
  const [activeOrbit9Section, setActiveOrbit9Section] = useState<string | null>(null);
  const [activeEspaceSection, setActiveEspaceSection] = useState<EspaceSection>("idees");
  const [activeReseauSection, setActiveReseauSection] = useState<ReseauSection>("profil");
  const [activeBlueprintLiveSection, setActiveBlueprintLiveSection] = useState<BlueprintLiveSection>("live");
  const [activeEntrepriseSection, setActiveEntrepriseSection] = useState<EntrepriseSection>("blueprint");
  const [activeBlueprintSubTab, setActiveBlueprintSubTab] = useState<BlueprintSubTab>("chantiers");
  const [activeEquipeSection, setActiveEquipeSection] = useState<EquipeSection>("bots");
  const [activeBureauSection, setActiveBureauSection] = useState<BureauSection>("taches");
  const [activeDiscussionTab, setActiveDiscussionTab] = useState<DiscussionTab>("overview");
  const [activeDeptTab, setActiveDeptTab] = useState("cockpit");
  const [activeAdminTab, setActiveAdminTab] = useState("dashboard");
  const [chatSourceView, setChatSourceView] = useState<string | null>(null);
  const [leftSidebarCollapsed, setLeftCollapsed] = useState(false);
  const [rightSidebarCollapsed, setRightCollapsed] = useState(false);
  // Auth state synced from AuthContext (F4)
  const auth = useAuth();
  const [isAuthenticated, setAuthenticatedState] = useState(() => auth.isAuthenticated);

  // Sync when AuthContext changes
  useEffect(() => {
    setAuthenticatedState(auth.isAuthenticated);
  }, [auth.isAuthenticated]);

  const setAuthenticated = useCallback((v: boolean) => {
    setAuthenticatedState(v);
    if (!v) {
      auth.logout();
    }
    try {
      if (v) localStorage.setItem("ghostx-auth", "authenticated");
      else localStorage.removeItem("ghostx-auth");
    } catch { /* noop */ }
  }, [auth]);
  const [isOnboarded, setOnboardedState] = useState(true);      // DEV: bypass onboarding

  const setOnboarded = useCallback((v: boolean) => {
    setOnboardedState(v);
    try { localStorage.setItem("ghostx-onboarded", String(v)); }
    catch { /* noop */ }
  }, []);

  // Simulation CREDO dans CenterZone — active quand onboarding sera live
  const [hasSeenSimulation, setHasSeenSimulationState] = useState(true); // DEV: bypass

  const setHasSeenSimulation = useCallback((v: boolean) => {
    setHasSeenSimulationState(v);
    try { localStorage.setItem("ghostx-simulation-done", String(v)); }
    catch { /* noop */ }
  }, []);

  const leftPanelRef = useRef<{ collapse: () => void; expand: () => void } | null>(null);

  const setActiveBot = useCallback((bot: BotInfo) => {
    setActiveBotState(bot);
    setActiveBotCode(bot.code);
  }, []);

  const navigateOrbit9 = useCallback((sectionId: string) => {
    setActiveOrbit9Section(sectionId);
    setActiveView("orbit9-detail");
  }, []);

  const navigateEspace = useCallback((section: EspaceSection) => {
    setActiveEspaceSection(section);
    setActiveView("espace-bureau");
  }, []);

  const navigateReseau = useCallback((section: ReseauSection) => {
    setActiveReseauSection(section);
    setActiveView("mon-reseau");
  }, []);

  const navigateBlueprint = useCallback((section: BlueprintLiveSection) => {
    setActiveBlueprintLiveSection(section);
    setActiveView("blueprint");
  }, []);

  const navigateEntreprise = useCallback((section: EntrepriseSection) => {
    setActiveEntrepriseSection(section);
    setActiveView("mon-entreprise");
  }, []);

  const navigateBlueprintSubTab = useCallback((tab: BlueprintSubTab) => {
    setActiveBlueprintSubTab(tab);
  }, []);

  const navigateEquipe = useCallback((section: EquipeSection) => {
    setActiveEquipeSection(section);
    setActiveView("mon-equipe");
  }, []);

  const navigateBureau = useCallback((section: BureauSection) => {
    setActiveBureauSection(section);
    // Synchroniser activeEspaceSection pour les sections partagees
    const bureauToEspace: Record<string, EspaceSection> = {
      "taches": "taches",
      "documents": "documents",
      "agenda": "agenda",
      "blueprint-perso": "idees",
      "discussions": "discussions",
      "notifications": "notifications",
    };
    setActiveEspaceSection(bureauToEspace[section] || "idees");
    setActiveView("espace-bureau");
  }, []);

  const navigateDiscussionTab = useCallback((tab: DiscussionTab) => {
    setActiveDiscussionTab(tab);
  }, []);

  const navigateDeptTab = useCallback((tab: string) => {
    setActiveDeptTab(tab);
  }, []);

  const navigateAdminTab = useCallback((tab: string) => {
    setActiveAdminTab(tab);
    setActiveView("admin");
  }, []);

  const navigateReglagesTab = useCallback((tab: string) => {
    setActiveAdminTab(tab);
    setActiveView("reglages");
  }, []);

  const navigateToChat = useCallback((sourceView: string) => {
    setChatSourceView(sourceView);
    setActiveView("live-chat");
  }, []);

  const navigateToDepartment = useCallback((botCode: string, view: ActiveView = "department") => {
    setActiveBotCode(botCode);
    setActiveView(view);
    // Reset dept tab quand on change de departement (sidebar click)
    setActiveDeptTab("cockpit");
  }, []);

  const registerLeftPanel = useCallback((api: { collapse: () => void; expand: () => void }) => {
    leftPanelRef.current = api;
  }, []);

  const toggleLeftSidebar = useCallback(() => {
    if (leftPanelRef.current) {
      if (leftSidebarCollapsed) {
        leftPanelRef.current.expand();
      } else {
        leftPanelRef.current.collapse();
      }
    }
    setLeftCollapsed((v) => !v);
  }, [leftSidebarCollapsed]);

  const toggleRightSidebar = useCallback(
    () => setRightCollapsed((v) => !v),
    []
  );

  return (
    <FrameMasterContext.Provider
      value={{
        activeBot,
        activeBotCode,
        activeView,
        activeOrbit9Section,
        activeEspaceSection,
        activeReseauSection,
        activeBlueprintLiveSection,
        activeEntrepriseSection,
        activeBlueprintSubTab,
        activeEquipeSection,
        activeBureauSection,
        activeDiscussionTab,
        activeDeptTab,
        activeAdminTab,
        chatSourceView,
        leftSidebarCollapsed,
        rightSidebarCollapsed,
        isAuthenticated,
        isOnboarded,
        hasSeenSimulation,
        currentUser: auth.user?.nom || "Carl Fugere",
        setActiveBot,
        setActiveView,
        navigateToDepartment,
        toggleLeftSidebar,
        toggleRightSidebar,
        setAuthenticated,
        setOnboarded,
        setHasSeenSimulation,
        setLeftCollapsed,
        navigateOrbit9,
        navigateEspace,
        navigateReseau,
        navigateBlueprint,
        navigateEntreprise,
        navigateBlueprintSubTab,
        navigateEquipe,
        navigateBureau,
        navigateDiscussionTab,
        navigateDeptTab,
        navigateAdminTab,
        navigateReglagesTab,
        navigateToChat,
        setActiveBotCode,
        registerLeftPanel,
      }}
    >
      {children}
    </FrameMasterContext.Provider>
  );
}

export function useFrameMaster(): FrameMasterContextType {
  const ctx = useContext(FrameMasterContext);
  if (!ctx)
    throw new Error("useFrameMaster must be inside FrameMasterProvider");
  return ctx;
}
